import { THEME } from '@common/constants/theme.constant';
import { Field, IFieldProps } from '@common/general-ui/field/field.component';
import { ConfigProvider, Transfer as AntdTransfer } from 'antd';
import type { TransferDirection } from 'antd/es/transfer';
import React, { ReactNode, useEffect, useState } from 'react';
import { css } from 'twin.macro';

export type ITransferChangeHandler = (e: { value: string[]; name: string }) => void;

export interface ITransferItem {
    key: string;
    title: string;
    description?: string;
}

export interface ITransferProps<TName = string> extends IFieldProps<string[], TName> {
    dataSource?: ITransferItem[];
    onChange?: ITransferChangeHandler;
    onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
    className?: string;
    emptyText?: string;
    resetTargetKeys?: boolean;
    showSelectAll?: boolean;
    showSearch?: boolean;
    titles?: string[];
    selectedTargetKeys?: string[];
}

export const Transfer = (props: ITransferProps): JSX.Element => {
    const {
        dataSource,
        emptyText,
        className,
        onChange,
        onBlur,
        name,
        resetTargetKeys,
        showSelectAll = false,
        showSearch = false,
        titles,
        selectedTargetKeys,
    } = props;

    const [targetKeys, setTargetKeys] = useState<string[]>(selectedTargetKeys || []);
    const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

    useEffect(() => {
        if (resetTargetKeys) {
            setTargetKeys([]);
        }
    }, [resetTargetKeys]);

    const handleChange = (nextTargetKeys: string[], _direction: TransferDirection, _moveKeys: string[]): void => {
        setTargetKeys(nextTargetKeys);
        onChange && onChange({ value: nextTargetKeys, name });
    };

    const onSelectChange = (sourceSelectedKeys: string[], targetSelectedKeys: string[]): void => {
        setSelectedKeys([...sourceSelectedKeys, ...targetSelectedKeys]);
    };

    return (
        <Field {...props}>
            <div onBlur={onBlur} tw="relative">
                <ConfigProvider renderEmpty={emptyText ? (): ReactNode => <>{emptyText}</> : undefined}>
                    <AntdTransfer
                        className={className}
                        dataSource={dataSource}
                        titles={titles?.length ? titles : []}
                        targetKeys={targetKeys}
                        selectedKeys={selectedKeys}
                        onChange={handleChange}
                        onSelectChange={onSelectChange}
                        showSelectAll={showSelectAll}
                        showSearch={showSearch}
                        render={(item): string => item.title}
                        css={styles.root}
                    />
                </ConfigProvider>
            </div>
        </Field>
    );
};

const styles = {
    root: css`
        .ant-transfer-list {
            border-radius: 4px;
            padding: 6px 12px;
            width: 50%;
        }
        .ant-transfer-list-content-item {
            padding: 0;
            padding-top: 2px;
            font-weight: 300;
            font-size: 14px;
            min-height: 24px;
        }
        .ant-transfer-list-content-item:hover {
            background-color: white;
        }
        .ant-transfer-list-content-item-checked {
            background-color: white;
            :hover {
                background-color: white !important;
            }
        }
        .ant-checkbox-checked {
            :: after {
                border-color: ${THEME.LIGHT_GREEN};
            }
        }
        .ant-checkbox-checked .ant-checkbox-inner {
            background-color: ${THEME.LIGHT_GREEN};
            border-color: ${THEME.LIGHT_GREEN};
            :hover {
                border-color: ${THEME.LIGHT_GREEN};
            }
        }
        .ant-transfer-operation {
            margin: 0 16px;
        }
        .ant-btn-primary:not([disabled]) {
            background: ${THEME.ORANGE};
            border-color: ${THEME.ORANGE};
            color: ${THEME.TEXT_BLACK};
        }
        .ant-btn {
            height: 40px;
            width: 40px;
        }
        .anticon {
            display: flex;
            justify-content: center;
        }
    `,
};
